import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link } from 'react-router-dom';
import { Capacitor } from "@capacitor/core";
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://dragon-fit.vercel.app';

const AuthContext = createContext(null);
const useAuth = () => useContext(AuthContext);

const openAppIfMobile = (shareId) => {
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  if (!isMobile) return; // solo móviles

  const appUrl = `dragonfit:///${shareId}`; 
  const fallbackUrl = window.location.href;

  const now = Date.now();
  const timeout = setTimeout(() => {
    // si la app no se abrió en 1s, se queda en la web
    window.location.href = fallbackUrl;
  }, 1000);

  // dispara la app
  window.location.href = appUrl;

  // limpieza en caso de que funcione
  const check = () => clearTimeout(timeout);
  window.addEventListener("pagehide", check);
};

function AppUrlHandler() {
  const navigate = useNavigate();

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      // App abierta o en background
      const listener = CapacitorApp.addListener("appUrlOpen", (event) => {
        const url = new URL(event.url);
        if (url.pathname.startsWith("/share/")) {
          navigate(url.pathname);
        }
      });

      // App abierta por primera vez
      CapacitorApp.getLaunchUrl().then((data) => {
        if (data?.url) {
          const url = new URL(data.url);
          if (url.pathname.startsWith("/share/")) {
            navigate(url.pathname);
          }
        }
      });

      return () => listener.remove();
    }
  }, [navigate]);

  return null;
}

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
  try {
    const response = await apiFetch("/api/auth/me");

    if (response.ok) {
      const userData = await response.json();
      setUser(userData);
    }
  } catch (error) {
    console.error("Auth check failed:", error);
  } finally {
    setLoading(false);
  }
};


  const login = (userData, token) => {
    setUser(userData);
    if (token) {
      localStorage.setItem('token', token);
    }
  };

  const logout = async () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

const AuthCallback = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await apiFetch("/api/auth/session", {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ session_id: sessionId })
          });

          if (response.ok) {
            const data = await response.json();
            login(data.user, data.token);
            navigate('/dashboard', { replace: true });
            return;
          }
        } catch (error) {
          console.error('Auth callback error:', error);
        }
      }
      navigate('/login', { replace: true });
    };

    processAuth();
  }, [navigate, login]);

  return (
    <div className="loading" style={{ minHeight: '100vh' }}>
      <div className="loading-spinner"></div>
    </div>
  );
};

const ShareImportPage = () => {
  const shareId = getShareId();
  const [workout, setWorkout] = useState(null);
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { t } = useTranslation();
  const [buttonLoading, setButtonLoading] = useState(false);
  

  useEffect(() => { fetchWorkout(); }, []);

  useEffect(() => {
    if (!loading && !user) {
      navigate("/login", { state: { redirect: `/share/${shareId}` } });
    }
  }, [loading, user]);

  const fetchWorkout = async () => {
    const res = await fetch(`${API_URL}/api/share/${shareId}`);
    if (res.ok) setWorkout(await res.json());
  };

  const handleImport = async () => {
    if (!user) {
      navigate("/login", { state: { redirect: `/share/${shareId}` } });
      return;
    }
     setButtonLoading(true);

    try {
    const res = await apiFetch(`/api/share/${shareId}/import`, { method: "POST" });
      if (res.ok) {
        // Abrir app móvil tras completar la importación
        openAppIfMobile(shareId);
        navigate("/dashboard");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setButtonLoading(false); // desactiva animación
    }
  };

  if (!workout) return <div>{t('share_import.loading')}</div>;

  return (
    <div className="page-container full-screen-center" style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      backgroundColor: '#000000', // fondo negro uniforme
      textAlign: 'center',
      padding: '20px',
    }}>
      <img src="/dragon-logo.png" alt="DragonFit" className="auth-logo" style={{
          width: '120px',
          marginBottom: '20px'
      }} />
      <h1 className="auth-title neon-text" style={{ marginBottom: '10px', fontSize: '2rem' }}>
          DragonFit
      </h1>
      <p className="auth-subtitle" style={{ marginBottom: '40px', fontSize: '1.2rem', color: '#fff' }}>
          {t('share_import.subtitle')}
      </p>

      <h2 style={{ color: '#fff', marginBottom: '10px' }}>{workout.name}</h2>
      <p style={{ color: '#ddd', marginBottom: '30px' }}>{workout.description}</p>

      <button
        className="btn btn-primary"
        onClick={handleImport}
        style={{
          position: 'relative',
          padding: '15px 30px',
          fontSize: '1rem',
          borderRadius: '25px',
          backgroundColor: '#00FF00',
          color: '#000',
          fontWeight: 'bold',
          boxShadow: '0 0 10px #00FF00',
          cursor: 'pointer',
          minWidth: '250px'
        }}
        disabled={buttonLoading} // deshabilitado mientras se ejecuta
      >
        {buttonLoading ? (
          <span
            className="spinner"
            style={{
              display: 'inline-block',
              width: '20px',
              height: '20px',
              border: '3px solid #000',
              borderTop: '3px solid #00FF00',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}
          />
        ) : (
          user ? t('share_import.add_btn') : t('share_import.login_btn')
        )}
</button>
  </div>
  );
};


const getShareId = () => {

  if (window.location.hash.includes("/share/")) {
    return window.location.hash.split("/")[2];
  }

  return window.location.pathname.split("/")[2];
};

const apiFetch = async (endpoint, options = {}) => {
  const token = localStorage.getItem("token");

  const headers = {
    "Content-Type": "application/json",
    ...(options.headers || {}),
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  return fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers,
  });
};



const ShareImportPageWrapper = () => (
  <AuthProvider>
    <AppUrlHandler />
    <ShareImportPage />
  </AuthProvider>
);

export default ShareImportPageWrapper;